import { CHECKS_LOGISTICA, NOMBRES_BLOQUES } from '../data/constantes'
import { PLANTILLAS, NIVELES_INFO } from '../data/niveles'
import type { Bloque, Clase, EjercicioAsignado, NivelClase, Recurso } from '../types'
import { nuevoId } from './id'

export function hoyISO(): string {
  const d = new Date()
  const mes = `${d.getMonth() + 1}`.padStart(2, '0')
  const dia = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${mes}-${dia}`
}

export function formatearFecha(iso: string): string {
  if (!iso) return 'Sin fecha'
  const partes = iso.split('-')
  if (partes.length !== 3) return iso
  return `${partes[2]}/${partes[1]}/${partes[0]}`
}

export function formatearFechaHora(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const dia = `${d.getDate()}`.padStart(2, '0')
  const mes = `${d.getMonth() + 1}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${dia}/${mes}/${d.getFullYear()} ${hh}:${mm}`
}

export function bloqueVacio(nombre: string, minutos = 0, objetivo = ''): Bloque {
  return { uid: nuevoId('blq'), nombre, minutos, objetivo, notas: '', ejercicios: [] }
}

export function bloquesEnBlanco(): Bloque[] {
  return NOMBRES_BLOQUES.map((n) => bloqueVacio(n))
}

export function bloquesDesdePlantilla(nivel: NivelClase): Bloque[] {
  const plantilla = PLANTILLAS[NIVELES_INFO[nivel].plantillaId]
  const desdePlantilla = plantilla.bloques.map((b) => bloqueVacio(b.nombre, b.minutos, b.objetivo))
  const faltantes = NOMBRES_BLOQUES.filter(
    (n) => !plantilla.bloques.some((b) => b.nombre === n)
  ).map((n) => bloqueVacio(n))
  const orden = (nombre: string) => NOMBRES_BLOQUES.indexOf(nombre)
  return [...desdePlantilla, ...faltantes].sort((a, b) => orden(a.nombre) - orden(b.nombre))
}

export function duracionPlantilla(nivel: NivelClase): number {
  return PLANTILLAS[NIVELES_INFO[nivel].plantillaId].duracion
}

export function claseNueva(): Clase {
  const checks: Record<string, boolean> = {}
  CHECKS_LOGISTICA.forEach((c) => {
    checks[c.id] = false
  })
  const ahora = new Date().toISOString()
  return {
    id: nuevoId('clase'),
    schemaVersion: 1,
    titulo: '',
    fecha: hoyISO(),
    grupo: '',
    poblacion: 'Adultos',
    nivel: 'Introducción',
    autonomia: 'En aprendizaje',
    participantes: 8,
    puntos: 4,
    duracion: 60,
    tipo: 'Clase habitual',
    altura: 'Baja altura',
    objetivo: 'Introducción',
    objetivoPersonalizado: '',
    consideraciones: '',
    logistica: { checks, estrategia: 'Individual', profes: 1, nota: '' },
    bloques: bloquesEnBlanco(),
    recordatorio: '',
    estado: 'Borrador',
    evaluacion: null,
    creadoEn: ahora,
    modificadoEn: ahora
  }
}

export function ejercicioDesdeRecurso(recurso: Recurso): EjercicioAsignado {
  return {
    uid: nuevoId('ej'),
    recursoId: recurso.id,
    nombre: `${recurso.id} · ${recurso.nombre}`,
    minutos: recurso.duracionMin,
    seriesRepes: '',
    lado: recurso.bilateral ? 'Ambos' : 'No aplica',
    modalidad: 'Grupo completo',
    variacion: 'Base',
    notas: ''
  }
}

export function ejercicioPersonalizadoVacio(): EjercicioAsignado {
  return {
    uid: nuevoId('ej'),
    recursoId: null,
    nombre: '',
    minutos: 5,
    seriesRepes: '',
    lado: 'No aplica',
    modalidad: 'Grupo completo',
    variacion: 'Base',
    notas: ''
  }
}

export function minutosPlanificados(clase: Clase): number {
  return clase.bloques.reduce((total, b) => total + (Number(b.minutos) || 0), 0)
}

export function duplicarClase(clase: Clase, opciones?: { paraProxima?: boolean }): Clase {
  const ahora = new Date().toISOString()
  const copia: Clase = {
    ...clase,
    id: nuevoId('clase'),
    titulo: opciones?.paraProxima ? `${clase.titulo} — próxima clase` : `${clase.titulo} (copia)`,
    fecha: opciones?.paraProxima ? '' : clase.fecha,
    estado: 'Borrador',
    evaluacion: null,
    recordatorio: opciones?.paraProxima ? '' : clase.recordatorio,
    creadoEn: ahora,
    modificadoEn: ahora,
    logistica: { ...clase.logistica, checks: { ...clase.logistica.checks } },
    bloques: clase.bloques.map((b) => ({
      ...b,
      uid: nuevoId('blq'),
      ejercicios: b.ejercicios.map((e) => ({ ...e, uid: nuevoId('ej') }))
    }))
  }
  return copia
}

export function normalizarClase(entrada: Partial<Clase>): Clase {
  const base = claseNueva()
  const clase: Clase = {
    ...base,
    ...entrada,
    id: entrada.id || base.id,
    schemaVersion: 1,
    logistica: {
      ...base.logistica,
      ...(entrada.logistica || {}),
      checks: { ...base.logistica.checks, ...(entrada.logistica?.checks || {}) }
    },
    bloques: Array.isArray(entrada.bloques) && entrada.bloques.length ? entrada.bloques : base.bloques,
    evaluacion: entrada.evaluacion ?? null
  }
  return clase
}
